import type { MenuNode } from '../api/navigationApi';

export type MenuLeaf = MenuNode & { path: string };

/** Every leaf of the tree, in display order. */
export function menuLeaves(nodes: readonly MenuNode[]): MenuLeaf[] {
  return nodes.flatMap((node) =>
    node.path !== null ? [node as MenuLeaf] : menuLeaves(node.children),
  );
}

/** The same tree keeping only the leaves `keep` accepts, minus emptied groups. */
export function pruneMenu(
  nodes: readonly MenuNode[],
  keep: (leaf: MenuLeaf) => boolean,
): MenuNode[] {
  return nodes.flatMap((node) => {
    if (node.path !== null) return keep(node as MenuLeaf) ? [node] : [];
    const children = pruneMenu(node.children, keep);
    return children.length ? [{ ...node, children }] : [];
  });
}

/**
 * Whether the nav entry at `path` is the active one for `pathname`.
 *
 * `NavLink`'s default prefix matching would light up both `/novedades` and
 * `/novedades/gestion`, because one path is a prefix of the other. The rule
 * here: a path is active on its own route and on nested detail routes, but the
 * moment the URL belongs to a more specific nav entry it yields to it.
 *
 * Passing the paths of the rendered entries (`allPaths`) is what makes the
 * specificity test possible. Home (`/`) is not special-cased: it only matches
 * exactly, since `${path}/` is `//`.
 */
export function isNavPathActive(
  pathname: string,
  path: string,
  allPaths: readonly string[],
): boolean {
  if (pathname === path) return true;
  if (!pathname.startsWith(`${path}/`)) return false;
  return !allPaths.some(
    (other) => other !== path && (pathname === other || pathname.startsWith(`${other}/`)),
  );
}

/** Whether any leaf under `node` is active — a group opens itself for it. */
export function containsActive(
  node: MenuNode,
  pathname: string,
  allPaths: readonly string[],
): boolean {
  return menuLeaves([node]).some((leaf) => isNavPathActive(pathname, leaf.path, allPaths));
}
